import { Head, useForm, usePage } from '@inertiajs/react'
import { Badge, Button, Group, Paper, Stack, Text, TextInput, Textarea, Title } from '@mantine/core'
import { type FormEvent, useState } from 'react'
import PageLayout from '@/components/PageLayout'
import type { SkillShowPageProps } from '@/types'

export default function SkillShow() {
  const { skill, versions, flash, csrf_token } = usePage<SkillShowPageProps>().props
  const [editing, setEditing] = useState(false)

  const { data, setData, patch, processing, reset } = useForm({
    skill: {
      name: skill.name,
      description: skill.description,
      body: skill.body || '',
      summary: '',
    },
    authenticity_token: csrf_token || '',
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    patch(`/skills/${skill.id}`, {
      preserveScroll: true,
      onSuccess: () => {
        setEditing(false)
        reset()
      },
    })
  }

  return (
    <>
      <Head title={skill.name} />

      <PageLayout
        title={skill.name}
        subtitle={skill.description}
        breadcrumbs={[
          { label: 'Skills', href: `/skills` },
          { label: skill.name },
        ]}
        flash={flash}
      >
        <Stack gap="xl">
          <Paper p="xl">
            <Stack gap="md">
              <Group justify="space-between" align="flex-start">
                <div>
                  <Text c="dimmed" size="sm">{skill.slug}</Text>
                  {skill.version_string ? <Text c="dimmed" size="sm">v{skill.version_string}</Text> : null}
                </div>
                <Group>
                  {skill.system ? <Badge color="blue" variant="light">System</Badge> : null}
                  {!skill.system && !editing ? (
                    <Button variant="default" onClick={() => setEditing(true)}>
                      Edit
                    </Button>
                  ) : null}
                </Group>
              </Group>

              {skill.tools.length > 0 ? (
                <Group gap="xs">
                  {skill.tools.map((tool) => (
                    <Badge key={tool} variant="light" color="gray">{tool}</Badge>
                  ))}
                </Group>
              ) : null}

              {editing ? (
                <Paper component="form" onSubmit={handleSubmit} p="lg" withBorder>
                  <Stack gap="md">
                    <TextInput
                      id="edit-name"
                      label="Name"
                      value={data.skill.name}
                      onChange={(e) => setData('skill', { ...data.skill, name: e.target.value })}
                    />

                    <TextInput
                      id="edit-desc"
                      label="Description"
                      value={data.skill.description}
                      onChange={(e) => setData('skill', { ...data.skill, description: e.target.value })}
                    />

                    <Textarea
                      id="edit-body"
                      label="Instructions"
                      autosize
                      minRows={12}
                      value={data.skill.body}
                      onChange={(e) => setData('skill', { ...data.skill, body: e.target.value })}
                    />

                    <TextInput
                      id="edit-summary"
                      label="Change summary"
                      placeholder="What changed?"
                      value={data.skill.summary}
                      onChange={(e) => setData('skill', { ...data.skill, summary: e.target.value })}
                    />

                    <Group>
                      <Button type="submit" loading={processing}>Save</Button>
                      <Button type="button" variant="default" onClick={() => { reset(); setEditing(false) }}>
                        Cancel
                      </Button>
                    </Group>
                  </Stack>
                </Paper>
              ) : skill.body ? (
                <Paper p="lg" withBorder>
                  <Text component="pre" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                    {skill.body}
                  </Text>
                </Paper>
              ) : (
                <Text c="dimmed" size="sm">No instructions yet.</Text>
              )}
            </Stack>
          </Paper>

          {versions.length > 0 ? (
            <div>
              <Title order={3} mb="md">Version history</Title>
              <Stack gap="sm">
                {versions.map((version) => (
                  <Paper key={version.id} p="md" withBorder>
                    <Group justify="space-between" align="flex-start">
                      <div>
                        <Text fw={600}>v{version.version}</Text>
                        <Text c="dimmed" size="sm">{version.summary || 'No summary'}</Text>
                      </div>
                      <Text c="dimmed" size="sm">{new Date(version.created_at).toLocaleDateString()}</Text>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </div>
          ) : null}
        </Stack>
      </PageLayout>
    </>
  )
}
